import { createSlice } from "@reduxjs/toolkit";

import { CLIENT_KEYS } from "../../constants/clientKeys";
import { callApi } from "../../utils/callApi";
import { showError, showSuccess } from "../../utils/notification";
import { queryClient } from "../../utils/reactQueryClient";
import { UploadProgress, uploadWithProgress } from "../../utils/uploadWithProgress";

// Initial state for transcription tasks
const initialState = {
  activeTasks: {}, // Map of taskId to task details
  completedTasks: {}, // Map of taskId to completed task results
};

// Create the transcription slice
const transcriptionSummarySlice = createSlice({
  name: "transcription-summary",
  initialState,
  reducers: {
    // Add a new transcription task
    addTask: (state, action) => {
      const { taskId, caseNumber, metadata } = action.payload;
      state.activeTasks[taskId] = {
        taskId,
        status: "PENDING",
        startTime: Date.now(),
        caseNumber,
        metadata,
      };
    },

    // Update task status
    updateTaskStatus: (state, action) => {
      const { taskId, status, result } = action.payload;
      if (state.activeTasks[taskId]) {
        state.activeTasks[taskId].status = status;
        if (result) {
          state.activeTasks[taskId].result = result;
        }
      }
    },

    // Complete a task and move it to completedTasks
    completeTask: (state, action) => {
      const { taskId, result } = action.payload;
      if (state.activeTasks[taskId]) {
        const task = state.activeTasks[taskId];
        state.completedTasks[taskId] = {
          ...task,
          result,
          endTime: Date.now(),
        };
        delete state.activeTasks[taskId];
      }
    },

    // Delete a completed task
    deleteCompletedTask: (state, action) => {
      const { taskId } = action.payload;
      if (state.completedTasks[taskId]) {
        delete state.completedTasks[taskId];
      }
    },
  },
});

export const { addTask, updateTaskStatus, completeTask, deleteCompletedTask } =
  transcriptionSummarySlice.actions;

/**
 * Kick off transcription + summarization for a multipart body.
 *
 * When `options.onUploadProgress` is provided (the Upload Recording modal), the
 * request goes over XHR so the browser can report byte-level upload progress;
 * otherwise it uses the standard `callApi`/fetch path (session-end, dictation).
 * `options.signal` aborts the in-flight upload.
 */
export const startTranscriptionSummaryTask =
  (formData, options = {}) =>
  async (dispatch) => {
    const { onUploadProgress, signal } = options as {
      onUploadProgress?: (progress: UploadProgress) => void;
      signal?: AbortSignal;
    };
    try {
      // Validate required form data fields before sending
      if (!formData.has("examinee_name") || !formData.get("examinee_name")) {
        showError("Examinee name is required for transcription", dispatch);
        throw new Error("Examinee name is required for transcription");
      }

      if (!formData.get("submission_id")) {
        const message =
          "This recording is no longer reserved. Download the audio, then start a new recording.";
        throw Object.assign(new Error(message), { detail: message });
      }
      const endpoint = "transcription/submissions";
      const response = onUploadProgress
        ? await uploadWithProgress(endpoint, formData, {
            onProgress: onUploadProgress,
            signal,
          })
        : await callApi(endpoint, "POST", null, formData);

      let caseNumber = null;
      let notes = "";
      const examineeName = formData.get("examinee_name");

      if (formData.has("case_number")) {
        caseNumber = formData.get("case_number");
      }

      if (formData.has("notes")) {
        notes = formData.get("notes") || ""; // Ensure notes is never null
      }

      // Extract task IDs from the response, handling the new response format
      // The response now contains a 'tasks' array with task objects that have task_id and source
      const taskIds = Array.isArray(response)
        ? response.map((task) => task.task_id)
        : response?.tasks
          ? response.tasks.map((task) => task.task_id)
          : [response.task_id];

      // Add each task to the store with notes, examinee_name, and source in metadata
      if (Array.isArray(response)) {
        response.forEach((task) => {
          dispatch(
            addTask({
              taskId: task.task_id,
              caseNumber,
              metadata: { notes, examineeName, source: task.source },
            })
          );
        });
      } else if (response?.tasks) {
        response.tasks.forEach((task) => {
          dispatch(
            addTask({
              taskId: task.task_id,
              caseNumber,
              metadata: { notes, examineeName, source: task.source },
            })
          );
        });
      } else {
        dispatch(
          addTask({
            taskId: response.task_id,
            caseNumber,
            metadata: { notes, examineeName },
          })
        );
      }

      // Overwriting a transcript blanks the previous transcript + summaries on
      // the server at task-start (MDRS-132). Invalidate the cached assessment now
      // so the UI reflects the cleared/TRANSCRIBING state immediately instead of
      // showing stale summaries until the poll completes.
      if (caseNumber) {
        queryClient.invalidateQueries({
          queryKey: [CLIENT_KEYS.ASSESSMENTS, "case", caseNumber],
        });
      }

      // Start polling for each task
      taskIds.forEach((taskId) => {
        dispatch(
          pollTranscriptionStatus({ taskId, caseNumber, notes, examineeName })
        );
      });

      return taskIds;
    } catch (error) {
      const detail = (error as { detail?: string })?.detail;
      showError(detail || "Transcription service is unavailable.", dispatch);
      throw error;
    }
  };

// Resume polling an ALREADY-started VM batch task on page load — no new
// /transcription/summarize call. Needed because the only code that persists the
// VM result runs inside the status-poll loop; if the doctor closed the tab while
// an assessment was still TRANSCRIBING, nothing polls the status endpoint and the
// summary + diarized transcript are never persisted (the row stays stuck at
// TRANSCRIBING). Callers must guard against a task that's already active (see the
// activeTasks check in AudioTranscript) so we don't double-poll in the tab that
// started it.
export const resumeTranscriptionSummaryTask =
  ({
    taskId,
    caseNumber = null,
    notes = "",
    examineeName = "",
  }: {
    taskId: string;
    caseNumber?: string | null;
    notes?: string | null;
    examineeName?: string | null;
  }) =>
  (dispatch) => {
    if (!taskId) return;
    dispatch(
      addTask({
        taskId,
        caseNumber,
        metadata: { notes, examineeName },
      })
    );
    dispatch(
      pollTranscriptionStatus({
        taskId,
        caseNumber,
        notes,
        examineeName,
        // Without a case there's no banner to show the failure, so keep the toast.
        quiet: !!caseNumber,
      })
    );
  };

export const MAX_POLL_DURATION_MS = 20 * 60 * 1000;

export const pollTranscriptionStatus =
  ({
    taskId,
    caseNumber = null,
    notes = "",
    examineeName = "",
    startedAt = Date.now(),
    quiet = false,
  }: {
    taskId: string;
    caseNumber: string | null | undefined;
    notes: string | null | undefined;
    examineeName?: string | null | undefined;
    /** Epoch ms when polling THIS task began; threaded through reschedules so
     *  the elapsed cap measures total time, not per-tick. */
    startedAt?: number;
    /** Resumed on page load: the failure banner reports a failure, not a toast. */
    quiet?: boolean;
  }) =>
  async (dispatch) => {
    // Stop polling once the cap is exceeded: fully clear the task (so it no
    // longer suppresses auto-export) and tell the user it's still processing
    // rather than looping silently forever. (MDRS-223)
    if (Date.now() - startedAt > MAX_POLL_DURATION_MS) {
      dispatch(completeTask({ taskId, result: { timedOut: true } }));
      dispatch(deleteCompletedTask({ taskId }));
      showError(
        `Transcription is taking longer than expected${
          caseNumber ? ` for Case ${caseNumber}` : ""
        }. It may still be processing — reopen the case later to check, or try again.`,
        dispatch
      );
      return;
    }
    try {
      let statusResponse;
      try {
        statusResponse = await callApi(
          `transcription/summary/status/${taskId}`,
          "GET"
        );
      } catch (error) {
        const errorResponse = error as { detail: string; status?: number };
        const status = errorResponse?.status;
        // A blip says nothing about the task; retry until the 20-min cap instead of dropping it.
        if (!status || status >= 500 || status === 408 || status === 429) {
          setTimeout(
            () =>
              dispatch(
                pollTranscriptionStatus({
                  taskId,
                  caseNumber,
                  notes,
                  examineeName,
                  startedAt,
                  quiet,
                })
              ),
            5000
          );
          return;
        }
        // completeTask first: deleteCompletedTask alone left the task "active" forever.
        dispatch(completeTask({ taskId, result: { failed: true } }));
        dispatch(deleteCompletedTask({ taskId }));
        if (caseNumber) {
          void queryClient.invalidateQueries({
            queryKey: [CLIENT_KEYS.ASSESSMENTS, "case", caseNumber],
          });
        }
        // 409: a newer recording replaced this one; it didn't fail.
        if (errorResponse?.status === 409 || quiet) return;

        // Show specific error message based on error type
        const errorMessage = errorResponse?.detail || "Unknown error occurred";
        showError(
          `Transcription failed${caseNumber ? ` for Case ${caseNumber}` : ""}: ${errorMessage}`,
          dispatch
        );
        return;
      }
      const data = statusResponse;

      // Update task status in the store
      dispatch(
        updateTaskStatus({
          taskId,
          status: data.state,
          result: data.status,
        })
      );

      if (data.state === "SUCCESS") {
        // If a new task_id is provided, use it for the next poll
        if (data.status && data.status.task_id) {
          dispatch(
            addTask({
              taskId: data.status.task_id,
              caseNumber,
              metadata: { notes, examineeName },
            })
          );
          dispatch(
            pollTranscriptionStatus({
              taskId: data.status.task_id,
              caseNumber,
              notes,
              examineeName,
              quiet,
            })
          );
        }

        // If we get a non-empty result, it's final
        else if (
          data.status &&
          typeof data.status === "object" &&
          Object.keys(data.status).length > 0
        ) {
          dispatch(completeTask({ taskId, result: data.status }));

          showSuccess(
            `Transcription & Summarization complete${
              caseNumber ? ` for Case ${caseNumber}` : ""
            }.`,
            dispatch
          );

          if (caseNumber) {
            await queryClient.invalidateQueries({
              queryKey: [CLIENT_KEYS.ASSESSMENTS, "case", caseNumber],
            });
          }

          // Delete the completed task after processing
          dispatch(deleteCompletedTask({ taskId }));
        }

        // State is SUCCESS but the result is still empty, so poll again
        else {
          setTimeout(
            () =>
              dispatch(
                pollTranscriptionStatus({
                  taskId,
                  caseNumber,
                  notes,
                  examineeName,
                  startedAt,
                  quiet,
                })
              ),
            2000
          );
        }
      } else if (data.state === "FAILURE") {
        // SCRIBE-06: the backend now attaches a safe, actionable message on
        // FAILURE (never the raw VM error text) — prefer it, but keep the
        // generic wording as a fallback for any other FAILURE shape.
        const errorMessage = data.status?.error_message;
        if (!quiet)
          showError(
            errorMessage
              ? `${errorMessage}${caseNumber ? ` (Case ${caseNumber})` : ""}`
              : `Transcription & Summarization failed${
                  caseNumber ? ` for Case ${caseNumber}` : ""
                }.`,
            dispatch
          );
        // SCRIBE-06: without this, the cached assessment can keep showing
        // "Finalising transcript..." for up to the React Query staleTime
        // after the backend already flipped status to FAILED — mirrors the
        // SUCCESS path's invalidation on the SUCCESS path.
        if (caseNumber) {
          queryClient.invalidateQueries({
            queryKey: [CLIENT_KEYS.ASSESSMENTS, "case", caseNumber],
          });
        }
        // Clear the task on terminal FAILURE (MDRS-223, review #767/#763): a
        // failed task left in activeTasks keeps suppressing auto-export and,
        // once resume-on-load exists, gets re-polled + re-toasted on every
        // reload. Same clean-up the timeout path does.
        dispatch(completeTask({ taskId, result: { failed: true } }));
        dispatch(deleteCompletedTask({ taskId }));
      } else {
        // If not yet in SUCCESS state, wait and poll again
        setTimeout(
          () =>
            dispatch(
              pollTranscriptionStatus({
                taskId,
                caseNumber,
                notes,
                examineeName,
                startedAt,
                quiet,
              })
            ),
          2000
        );
      }
    } catch (_err) {
      if (!quiet) {
        showError(
          `Unable to check transcription status${
            caseNumber ? ` for Case ${caseNumber}` : ""
          }.`,
          dispatch
        );
      }

      // In case of error, wait and retry polling for the same taskId
      setTimeout(
        () =>
          dispatch(
            pollTranscriptionStatus({
              taskId,
              caseNumber,
              notes,
              examineeName,
              startedAt,
              quiet,
            })
          ),
        5000
      );
    }
  };

export default transcriptionSummarySlice.reducer;
